const YOUTUBE_ID_LENGTH = 11;
const YOUTUBE_THUMBNAIL_BASE_URL = "https://img.youtube.com/vi";
const URL_SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:\/\//i;
const YOUTUBE_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/;
const TRAILING_URL_PUNCTUATION_PATTERN = /[),.;:!?]+$/;
const URL_CANDIDATE_PATTERN =
  /\b(?:https?:\/\/|www\.|youtube\.com\/|youtu\.be\/)[^\s<>"']+/gi;
const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "music.youtube.com",
  "youtu.be",
  "www.youtu.be",
]);
const YOUTUBE_SHORT_HOSTS = new Set(["youtu.be", "www.youtu.be"]);
const YOUTUBE_WATCH_PATH = "/watch";
const YOUTUBE_SHORTS_PATH_PREFIX = "/shorts/";
const YOUTUBE_EMBED_PATH_PREFIX = "/embed/";
const YOUTUBE_VIDEO_QUERY_KEY = "v";
const LINK_FORMAT_NAME = "link";

const createUrl = (urlText) => {
  try {
    const trimmedUrlText = removeTrailingUrlPunctuation(String(urlText).trim());
    const parseableUrl = URL_SCHEME_PATTERN.test(trimmedUrlText)
      ? trimmedUrlText
      : `https://${trimmedUrlText}`;

    return new URL(parseableUrl);
  } catch {
    return null;
  }
};

const removeTrailingUrlPunctuation = (urlText) =>
  urlText.replace(TRAILING_URL_PUNCTUATION_PATTERN, "");

const extractPathVideoId = ({ pathname, pathPrefix }) => {
  if (!pathname.startsWith(pathPrefix)) return null;

  const videoId = pathname.slice(pathPrefix.length).split("/")[0];
  return normalizeYoutubeVideoId(videoId);
};

const getWatchVideoId = (url) => {
  if (url.pathname !== YOUTUBE_WATCH_PATH) return null;

  return normalizeYoutubeVideoId(url.searchParams.get(YOUTUBE_VIDEO_QUERY_KEY));
};

const getYoutubeVideoIdFromUrl = (url) => {
  if (!YOUTUBE_HOSTS.has(url.hostname.toLowerCase())) return null;

  if (YOUTUBE_SHORT_HOSTS.has(url.hostname.toLowerCase())) {
    return normalizeYoutubeVideoId(url.pathname.split("/")[1]);
  }

  return (
    getWatchVideoId(url) ||
    extractPathVideoId({
      pathname: url.pathname,
      pathPrefix: YOUTUBE_SHORTS_PATH_PREFIX,
    }) ||
    extractPathVideoId({
      pathname: url.pathname,
      pathPrefix: YOUTUBE_EMBED_PATH_PREFIX,
    })
  );
};

const normalizeYoutubeVideoId = (videoId) => {
  if (!videoId || videoId.length !== YOUTUBE_ID_LENGTH) return null;

  return YOUTUBE_ID_PATTERN.test(videoId) ? videoId : null;
};

const splitUrlCandidate = (rawUrl) => {
  const url = removeTrailingUrlPunctuation(rawUrl);

  return {
    trailingText: rawUrl.slice(url.length),
    url,
  };
};

const pushTextSegment = ({ segments, text, isYoutubeLink }) => {
  if (!text) return segments;

  return [...segments, { isYoutubeLink, text }];
};

const buildSegmentAttributes = ({ attributes, segment }) => {
  if (!segment.isYoutubeLink) return attributes;

  return {
    ...attributes,
    [LINK_FORMAT_NAME]: segment.text,
  };
};

const transformTextOp = ({ Delta, op }) => {
  if (typeof op.insert !== "string") return new Delta().push(op);

  return createYoutubeLinkedTextDelta({
    Delta,
    attributes: op.attributes,
    text: op.insert,
  });
};

/**
 * Extracts the YouTube video ID from supported YouTube URL formats.
 *
 * @param {string} urlText - URL text to parse.
 * @returns {string | null} The 11-character YouTube video ID, or null.
 */
export const extractYoutubeVideoId = (urlText) => {
  const url = createUrl(urlText);
  if (!url) return null;

  return getYoutubeVideoIdFromUrl(url);
};

/**
 * Creates a public YouTube thumbnail URL for a video ID.
 *
 * @param {string} videoId - YouTube video ID.
 * @returns {string} Thumbnail image URL.
 */
export const getYoutubeThumbnailUrl = (videoId) =>
  `${YOUTUBE_THUMBNAIL_BASE_URL}/${videoId}/hqdefault.jpg`;

/**
 * Checks whether URL text points to a supported YouTube video URL.
 *
 * @param {string} urlText - URL text to check.
 * @returns {boolean} True when the URL contains a valid YouTube video ID.
 */
export const isYoutubeUrl = (urlText) =>
  Boolean(extractYoutubeVideoId(urlText));

/**
 * Splits text into plain and YouTube URL segments for Quill conversion.
 *
 * @param {string} text - Pasted text.
 * @returns {{ text: string, isYoutubeLink: boolean }[]} Text segments.
 */
export const splitYoutubeTextSegments = (text) => {
  const segments = [];
  let lastIndex = 0;

  for (const match of String(text).matchAll(URL_CANDIDATE_PATTERN)) {
    const rawUrl = match[0];
    const matchIndex = match.index ?? 0;
    const { trailingText, url } = splitUrlCandidate(rawUrl);

    if (!isYoutubeUrl(url)) continue;

    segments.push(
      ...pushTextSegment({
        segments: [],
        text: String(text).slice(lastIndex, matchIndex),
        isYoutubeLink: false,
      }),
      { isYoutubeLink: true, text: url },
      ...pushTextSegment({
        segments: [],
        text: trailingText,
        isYoutubeLink: false,
      })
    );
    lastIndex = matchIndex + rawUrl.length;
  }

  return pushTextSegment({
    segments,
    text: String(text).slice(lastIndex),
    isYoutubeLink: false,
  });
};

/**
 * Checks whether pasted text contains at least one YouTube link segment.
 *
 * @param {string} text - Pasted text.
 * @returns {boolean} True when text contains a supported YouTube URL.
 */
export const hasYoutubeTextSegment = (text) =>
  splitYoutubeTextSegments(text).some((segment) => segment.isYoutubeLink);

/**
 * Builds a Quill Delta that links only YouTube URL segments.
 *
 * @param {{ Delta: typeof import("quill-delta"), attributes?: object, text: string }} options - Delta build options.
 * @returns {import("quill-delta")} Delta with linked YouTube URL segments.
 */
export const createYoutubeLinkedTextDelta = ({ Delta, attributes, text }) =>
  splitYoutubeTextSegments(text).reduce((nextDelta, segment) => {
    const segmentAttributes = buildSegmentAttributes({ attributes, segment });
    return nextDelta.insert(segment.text, segmentAttributes);
  }, new Delta());

/**
 * Creates a Quill clipboard matcher that auto-links pasted YouTube URLs.
 *
 * @param {typeof import("quill-delta")} Delta - Quill Delta constructor.
 * @returns {(node: Node, delta: import("quill-delta")) => import("quill-delta")} Matcher.
 */
export const createYoutubeAutoLinkMatcher = (Delta) => (_node, delta) =>
  delta.reduce(
    (nextDelta, op) => nextDelta.concat(transformTextOp({ Delta, op })),
    new Delta()
  );
